import type {
  ClashCategoryFilter,
  ClashResult,
} from '@viewer/clash/lib'
import { sortBySeverity } from '@viewer/clash/lib'
import type { FilterQuery } from '@viewer/lib/filter'

// ---------------------------------------------------------------------------
// Conjuntos A x B: o coracao do painel de conflitos.
//
// Correr tudo contra tudo num modelo estrutural da centenas de conflitos
// "de construcao" (vigas a entrar nos pilares) que ninguem quer ver. O
// modelo school_str.ifc do ribbon da 929 sem filtros. Por isso a analise
// e SEMPRE definida por dois conjuntos, como no Navisworks: so se testam
// pares com um elemento de A contra um de B.
//
// Um lado define-se por:
//   - modelos (vazio = todos os carregados);
//   - categorias IFC (vazio = NENHUMA, ver abaixo), OU a seleccao actual
//     do viewer, OU uma REGRA do construtor de filtros (o Shift+F).
//
// A REGRA e o mesmo motor e a mesma UI do filtro multi-criterio
// (`@viewer/lib/filter` + `@viewer/filter/FilterQueryEditor`): E/OU sobre
// entidade IFC, piso, sistema, material, tipo, PredefinedType e
// propriedade. Nao ha linguagem nova: quem sabe usar o Shift+F sabe
// definir um conjunto de conflitos. Como o motor de regras e ASSINCRONO
// (percorre os modelos), o resultado dele entra aqui ja resolvido, em
// `ruleTargets`, e nao ha nada assincrono nestas funcoes.
//
// COMO ISTO CHEGA AO MOTOR. O motor da Fase 1 sabe filtrar por duas
// vias, e usam-se as duas quando aplicavel:
//   1. `ids` por modelo restringe a analise a uma lista de elementos.
//      E a poda mais forte e aplica-se sempre: passa-se a UNIAO de A e B,
//      por isso tudo o que nao pertence a nenhum dos lados nem chega a
//      entrar na grelha.
//   2. `categoryFilter.setA/setB` e uma matriz POR CATEGORIA. So se pode
//      usar quando as categorias dos dois lados sao disjuntas (Estrutura
//      x MEP e o caso tipico). Quando a mesma categoria esta dos dois
//      lados (por exemplo IFCBEAM do modelo 1 contra IFCBEAM do modelo
//      2) a matriz por categoria nao consegue exprimir a intencao e
//      fica-se pela poda por `ids`.
// Em qualquer dos casos os resultados passam pelo `pairAllowed`, que e a
// verdade final: a pertenca e por (modelo, localId), nao por categoria.
//
// SELF-CLASH. No motor, `selfClash` significa "testar pares dentro do
// MESMO modelo". Nao e um extra: numa federacao de um so ficheiro (o
// caso comum de um IFC exportado com tudo la dentro), "Estrutura x MEP"
// com self-clash desligado devolve ZERO, porque os dois lados vivem no
// mesmo modelo. Dai o `sidesOnlySameModel`, que a UI usa para avisar.
// ---------------------------------------------------------------------------

export type ClashSideSource = 'categories' | 'selection' | 'rule'

export interface ClashSide {
  source: ClashSideSource
  /** modelIds do lado. Vazio = todos os modelos carregados. */
  models: string[]
  /**
   * Regra do construtor de filtros, quando `source === 'rule'`. Fica
   * guardada com a configuracao do teste (e trabalho de quem analisa,
   * nao uma preferencia de UI: ver `clashSetupStorage`).
   */
  rule?: FilterQuery
  /**
   * Categorias IFC em MAIUSCULAS. Vazio = NENHUMA, e o lado fica vazio.
   *
   * Foi assim de proposito. A leitura "vazio = todas" e mais comoda mas
   * mente numa situacao que acontece: o preset "Estrutura x MEP" num
   * modelo so de estrutura resolve o lado MEP para uma lista vazia, e com
   * "vazio = todas" o utilizador pedia Estrutura x MEP e corria Estrutura
   * x TUDO, com centenas de conflitos de construcao e sem perceber
   * porque. Quem quer tudo carrega em "Todas" (ou no preset Tudo x Tudo),
   * que preenche a lista com o que o modelo tem mesmo.
   */
  categories: string[]
}

export function emptySide(): ClashSide {
  return { source: 'categories', models: [], categories: [] }
}

/** Inventario de um modelo: que categorias tem e com quantos elementos
 *  COM GEOMETRIA (os unicos que podem colidir). */
export interface ClashModelInventory {
  modelId: string
  fileName: string
  /** Categoria em maiusculas -> localIds com geometria. */
  byCategory: Map<string, number[]>
  /** Elementos com geometria, no total. */
  total: number
}

export interface SelectionLike {
  modelId: string
  localId: number
}

/** Membros efectivos de um lado: modelId -> conjunto de localIds. */
export type SideMembers = Map<string, Set<number>>

function modelsOfSide(
  side: ClashSide,
  inventories: readonly ClashModelInventory[],
): ClashModelInventory[] {
  if (side.models.length === 0) return [...inventories]
  const wanted = new Set(side.models)
  return inventories.filter((inv) => wanted.has(inv.modelId))
}

/**
 * Categorias efectivas de um lado: as escolhidas que existem mesmo nos
 * modelos do lado. Sem escolha nenhuma, nenhuma. Sempre ordenadas, para
 * a UI e o motor verem a mesma lista.
 */
export function effectiveCategories(
  side: ClashSide,
  inventories: readonly ClashModelInventory[],
): string[] {
  if (side.categories.length === 0) return []
  const invs = modelsOfSide(side, inventories)
  const present = new Set<string>()
  for (const inv of invs) {
    for (const cat of inv.byCategory.keys()) present.add(cat)
  }
  const wanted = new Set(side.categories.map((c) => c.toUpperCase()))
  return [...present].filter((c) => wanted.has(c)).sort()
}

/** Todas as categorias presentes nos modelos indicados (vazio = todos).
 *  E o que o botao "Todas" e os presets de federacao escrevem no lado. */
export function allCategoriesOf(
  modelIds: readonly string[],
  inventories: readonly ClashModelInventory[],
): string[] {
  const wanted = modelIds.length > 0 ? new Set(modelIds) : null
  const present = new Set<string>()
  for (const inv of inventories) {
    if (wanted && !wanted.has(inv.modelId)) continue
    for (const cat of inv.byCategory.keys()) present.add(cat)
  }
  return [...present].sort()
}

export interface CategoryCount {
  category: string
  count: number
}

/**
 * Categorias disponiveis nos modelos indicados (vazio = todos), com o
 * numero de elementos COM GEOMETRIA de cada uma. E isto que enche o
 * selector com "IFCBEAM (312)" em vez de uma lista onde metade das
 * entradas nao existe no ficheiro. Ordenado por contagem descendente:
 * quem escolhe um conjunto quer ver primeiro o que tem peso.
 */
export function categoryCounts(
  modelIds: readonly string[],
  inventories: readonly ClashModelInventory[],
): CategoryCount[] {
  const wanted = modelIds.length > 0 ? new Set(modelIds) : null
  const totals = new Map<string, number>()
  for (const inv of inventories) {
    if (wanted && !wanted.has(inv.modelId)) continue
    for (const [cat, ids] of inv.byCategory) {
      totals.set(cat, (totals.get(cat) ?? 0) + ids.length)
    }
  }
  return [...totals.entries()]
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category))
}

/** Contagem de elementos de um lado. E o numero que a UI mostra ao lado
 *  do conjunto ("Estrutura: 412 elementos"). */
export function sideCount(members: SideMembers): number {
  let n = 0
  for (const set of members.values()) n += set.size
  return n
}

/** Elementos COM GEOMETRIA de um modelo, do inventario. So estes podem
 *  colidir, por isso e por aqui que passa o resultado de uma regra. */
function geometricIdsOf(inv: ClashModelInventory): Set<number> {
  const out = new Set<number>()
  for (const ids of inv.byCategory.values()) {
    for (const id of ids) out.add(id)
  }
  return out
}

/**
 * Resolve um lado para os elementos concretos. Com `source: 'selection'`
 * o lado e a seleccao actual do viewer (limitada aos modelos do lado, se
 * algum estiver escolhido); com `'rule'` sao os elementos que a regra
 * apanhou (em `ruleTargets`, ja avaliada por quem chama), limitados aos
 * que TEM GEOMETRIA; com `'categories'` sao os elementos das categorias
 * efectivas.
 *
 * A regra passa pelo crivo da geometria e a seleccao nao: uma regra pode
 * apanhar milhares de elementos logicos (tipos, sistemas, agregados) e a
 * contagem em vivo tem de dizer quantos elementos VAO MESMO ser
 * testados. Uma seleccao vem do 3D e ja e, por construcao, geometrica.
 */
export function resolveSide(
  side: ClashSide,
  inventories: readonly ClashModelInventory[],
  selection: readonly SelectionLike[] = [],
  ruleTargets: readonly SelectionLike[] = [],
): SideMembers {
  const out: SideMembers = new Map()
  const invs = modelsOfSide(side, inventories)
  if (side.source === 'selection' || side.source === 'rule') {
    const fromRule = side.source === 'rule'
    const source = fromRule ? ruleTargets : selection
    const allowed = new Set(invs.map((i) => i.modelId))
    const geometric = fromRule
      ? new Map(invs.map((i) => [i.modelId, geometricIdsOf(i)]))
      : null
    for (const t of source) {
      if (!allowed.has(t.modelId)) continue
      if (geometric && !geometric.get(t.modelId)?.has(t.localId)) continue
      let set = out.get(t.modelId)
      if (!set) {
        set = new Set<number>()
        out.set(t.modelId, set)
      }
      set.add(t.localId)
    }
    return out
  }
  if (side.categories.length === 0) return out
  const wanted = new Set(side.categories.map((c) => c.toUpperCase()))
  for (const inv of invs) {
    let set = out.get(inv.modelId)
    for (const [cat, ids] of inv.byCategory) {
      if (!wanted.has(cat)) continue
      if (!set) {
        set = new Set<number>()
        out.set(inv.modelId, set)
      }
      for (const id of ids) set.add(id)
    }
  }
  return out
}

/** Uniao dos dois lados no formato que o `runClash` aceita em `ids`. */
export function unionIds(
  a: SideMembers,
  b: SideMembers,
): Record<string, number[]> {
  const merged = new Map<string, Set<number>>()
  for (const source of [a, b]) {
    for (const [modelId, ids] of source) {
      let set = merged.get(modelId)
      if (!set) {
        set = new Set<number>()
        merged.set(modelId, set)
      }
      for (const id of ids) set.add(id)
    }
  }
  const out: Record<string, number[]> = {}
  for (const [modelId, ids] of merged) {
    out[modelId] = [...ids].sort((x, y) => x - y)
  }
  return out
}

/** Os dois lados vivem todos num so e mesmo modelo? So entao, com o
 *  `selfClash` desligado, a analise sai vazia sem explicacao: com mais
 *  do que um modelo, os pares entre modelos diferentes correm na mesma
 *  (os 5 modelos do Cha139 contra os 5 deram 1154 duros). E nisto que a
 *  UI se baseia para avisar. */
export function sidesOnlySameModel(a: SideMembers, b: SideMembers): boolean {
  const modelos = new Set<string>()
  for (const [m, ids] of a) if (ids.size > 0) modelos.add(m)
  for (const [m, ids] of b) if (ids.size > 0) modelos.add(m)
  return modelos.size === 1
}

/** Os dois lados partilham algum modelo? (Os pares DENTRO desse modelo
 *  ficam de fora sem o `selfClash`, mas os outros correm.) */
export function sidesShareModel(a: SideMembers, b: SideMembers): boolean {
  for (const modelId of a.keys()) {
    if (b.has(modelId)) return true
  }
  return false
}

/**
 * Matriz por categoria para o motor, quando ela consegue exprimir os
 * dois lados: categorias disjuntas e nenhum dos lados vindo da
 * seleccao (uma seleccao mistura categorias arbitrarias). Devolve null
 * quando nao da, e ai a poda fica so pelos `ids` e pelo `pairAllowed`.
 */
export function deriveCategoryFilter(
  sideA: ClashSide,
  sideB: ClashSide,
  inventories: readonly ClashModelInventory[],
): ClashCategoryFilter | null {
  if (sideA.source !== 'categories' || sideB.source !== 'categories') return null
  const catsA = effectiveCategories(sideA, inventories)
  const catsB = effectiveCategories(sideB, inventories)
  if (catsA.length === 0 || catsB.length === 0) return null
  const setB = new Set(catsB)
  if (catsA.some((c) => setB.has(c))) return null
  return { setA: catsA, setB: catsB }
}

function inSide(members: SideMembers, modelId: string, localId: number): boolean {
  return members.get(modelId)?.has(localId) === true
}

/** Verdade final da matriz: o par tem um elemento em A e outro em B (em
 *  qualquer ordem)? O motor devolve os pares na ordem dele. */
export function pairAllowed(
  r: ClashResult,
  a: SideMembers,
  b: SideMembers,
): boolean {
  const { modelA, localIdA, modelB, localIdB } = r.pair
  const aFirst = inSide(a, modelA, localIdA) && inSide(b, modelB, localIdB)
  const bFirst = inSide(b, modelA, localIdA) && inSide(a, modelB, localIdB)
  return aFirst || bFirst
}

/** Aplica a matriz e o corte de contactos. Os contactos aparecem quando
 *  se liga a folga: dois elementos encostados tem distancia 0 e estao,
 *  por definicao, dentro de qualquer folga pedida. Sao ruido em modelos
 *  completos, dai o corte opcional. */
export function filterResults(
  results: readonly ClashResult[],
  opts: {
    a: SideMembers
    b: SideMembers
    /** Metros. Folgas com distancia abaixo disto sao descartadas. */
    ignoreContacts?: number
  },
): ClashResult[] {
  const cut = opts.ignoreContacts ?? 0
  return results
    .filter((r) => {
      if (!pairAllowed(r, opts.a, opts.b)) return false
      if (cut > 0 && r.kind === 'clearance' && (r.distance ?? 0) < cut) return false
      return true
    })
    .map((r) => orientToSides(r, opts.a, opts.b))
}

/** O motor devolve o par pela ordem dele (indice global), nao pela dos
 *  conjuntos. A UI pinta `pair.A` com a cor do conjunto A e escreve-o
 *  na primeira linha, por isso o par e virado quando so pertence pela
 *  ordem B-A. A `clashKey` e canonica e nao muda com a viragem. */
export function orientToSides(
  r: ClashResult,
  a: SideMembers,
  b: SideMembers,
): ClashResult {
  const { modelA, localIdA, modelB, localIdB } = r.pair
  if (inSide(a, modelA, localIdA) && inSide(b, modelB, localIdB)) return r
  if (!(inSide(b, modelA, localIdA) && inSide(a, modelB, localIdB))) return r
  return {
    ...r,
    pair: { modelA: modelB, localIdA: localIdB, modelB: modelA, localIdB: localIdA },
    categoryA: r.categoryB,
    categoryB: r.categoryA,
  }
}

// --- Presets ---------------------------------------------------------------
// Listas deliberadamente generosas: intersectam-se com o que o modelo
// tem mesmo (ver `applyPreset`), por isso uma categoria a mais nao custa
// nada e uma a menos deixava elementos de fora sem o utilizador dar por
// isso.

export const STRUCTURE_CATEGORIES = [
  'IFCBEAM',
  'IFCCOLUMN',
  'IFCSLAB',
  'IFCFOOTING',
  'IFCPILE',
  'IFCMEMBER',
  'IFCPLATE',
  'IFCWALL',
  'IFCWALLSTANDARDCASE',
  'IFCREINFORCINGBAR',
  'IFCREINFORCINGMESH',
  'IFCTENDON',
]

export const MEP_CATEGORIES = [
  'IFCDUCTSEGMENT',
  'IFCDUCTFITTING',
  'IFCDUCTSILENCER',
  'IFCPIPESEGMENT',
  'IFCPIPEFITTING',
  'IFCCABLECARRIERSEGMENT',
  'IFCCABLECARRIERFITTING',
  'IFCCABLESEGMENT',
  'IFCCABLEFITTING',
  'IFCAIRTERMINAL',
  'IFCAIRTERMINALBOX',
  'IFCVALVE',
  'IFCPUMP',
  'IFCFAN',
  'IFCBOILER',
  'IFCCHILLER',
  'IFCTANK',
  'IFCJUNCTIONBOX',
  'IFCOUTLET',
  'IFCSWITCHINGDEVICE',
  'IFCPROTECTIVEDEVICE',
  'IFCLIGHTFIXTURE',
  'IFCSANITARYTERMINAL',
  'IFCSPACEHEATER',
  'IFCUNITARYEQUIPMENT',
  'IFCFLOWSEGMENT',
  'IFCFLOWFITTING',
  'IFCFLOWTERMINAL',
  'IFCFLOWCONTROLLER',
  'IFCFLOWMOVINGDEVICE',
  'IFCFLOWSTORAGEDEVICE',
  'IFCFLOWTREATMENTDEVICE',
  'IFCENERGYCONVERSIONDEVICE',
  'IFCDISTRIBUTIONELEMENT',
  'IFCDISTRIBUTIONCONTROLELEMENT',
  'IFCDISTRIBUTIONFLOWELEMENT',
]

/** Categorias que so fazem ruido numa deteccao de conflitos: volumes
 *  logicos e vazios que atravessam tudo por construcao. */
export const NOISE_CATEGORIES = [
  'IFCSPACE',
  'IFCOPENINGELEMENT',
  'IFCVIRTUALELEMENT',
  'IFCANNOTATION',
  'IFCGRID',
  'IFCBUILDINGELEMENTPROXY',
]

/** So as categorias do preset que existem mesmo nos modelos. Sem isto o
 *  painel oferecia conjuntos vazios com ar de cheios. */
export function applyPreset(
  wanted: readonly string[],
  inventories: readonly ClashModelInventory[],
): string[] {
  const present = new Set<string>()
  for (const inv of inventories) {
    for (const cat of inv.byCategory.keys()) present.add(cat)
  }
  return wanted.filter((c) => present.has(c)).sort()
}

// --- Agrupamento da lista --------------------------------------------------

/** 'grupos': os grupos da triagem (agrupamento automatico, proposta ou
 *  edicao a mao), com o titulo do grupo. */
export type ClashGroupBy = 'severity' | 'categories' | 'models' | 'grupos'

export interface ClashGroup {
  key: string
  label: string
  results: ClashResult[]
}

/** Fronteiras de gravidade, em metros. Escolhidas para separar o que e
 *  claramente um erro de coordenacao (mais de 10 cm) do que pode ser
 *  folga de modelacao. `depth` e APROXIMADA (docs/CLASH.md): serve para
 *  ordenar e agrupar, nunca para medir. */
const SEVERITY_HIGH = 0.1
const SEVERITY_MID = 0.01

function severityBucket(r: ClashResult): { key: string; label: string } {
  if (r.kind === 'clearance') return { key: '3-folga', label: 'Folgas' }
  const d = r.depth ?? 0
  if (d >= SEVERITY_HIGH) {
    return { key: '0-grave', label: 'Graves (mais de 10 cm)' }
  }
  if (d >= SEVERITY_MID) {
    return { key: '1-medio', label: 'Médios (1 a 10 cm)' }
  }
  return { key: '2-ligeiro', label: 'Ligeiros (menos de 1 cm)' }
}

function catPair(r: ClashResult): { key: string; label: string } {
  const a = r.categoryA ?? 'sem categoria'
  const b = r.categoryB ?? 'sem categoria'
  const [x, y] = a <= b ? [a, b] : [b, a]
  return { key: `${x}|${y}`, label: `${x} x ${y}` }
}

function modelPair(
  r: ClashResult,
  nome: (modelId: string) => string = (id) => id,
): { key: string; label: string } {
  const a = r.pair.modelA
  const b = r.pair.modelB
  if (a === b) return { key: `${a}|${a}`, label: `${nome(a)} (mesmo modelo)` }
  const [x, y] = a <= b ? [a, b] : [b, a]
  return { key: `${x}|${y}`, label: `${nome(x)} x ${nome(y)}` }
}

/**
 * Agrupa e ordena. Dentro de cada grupo manda sempre a gravidade
 * (`sortBySeverity` da Fase 1); os grupos saem por chave, o que em
 * 'severity' e a ordem de gravidade e nos outros e ordem alfabetica
 * estavel.
 */
export function groupResults(
  results: readonly ClashResult[],
  groupBy: ClashGroupBy,
  /** Nome a mostrar de cada modelo (o nome curto); por omissao o id. */
  nomeDoModelo?: (modelId: string) => string,
  /** Grupo de cada conflito (para 'grupos'): id, titulo e ordem. */
  grupoDe?: (r: ClashResult) => { id: string; titulo: string; ordem: number } | null,
): ClashGroup[] {
  const bucketOf =
    groupBy === 'categories'
      ? catPair
      : groupBy === 'models'
        ? (r: ClashResult) => modelPair(r, nomeDoModelo)
        : groupBy === 'grupos'
          ? (r: ClashResult) => {
              const g = grupoDe?.(r)
              return g
                ? { key: `${String(g.ordem).padStart(1, '0')}|${g.titulo}|${g.id}`, label: g.titulo }
                : { key: '9|~sem grupo', label: 'Sem grupo' }
            }
          : severityBucket
  const groups = new Map<string, ClashGroup>()
  for (const r of results) {
    const { key, label } = bucketOf(r)
    let g = groups.get(key)
    if (!g) {
      g = { key, label, results: [] }
      groups.set(key, g)
    }
    g.results.push(r)
  }
  const out = [...groups.values()]
  for (const g of out) g.results = sortBySeverity(g.results)
  if (groupBy === 'severity' || groupBy === 'grupos') {
    // Nos grupos, a chave comeca pela ordem da prioridade (0 Critica a
    // 3 Baixa, 9 sem grupo).
    out.sort((a, b) => a.key.localeCompare(b.key))
  } else {
    // Grupos maiores primeiro: e por onde se comeca a triar.
    out.sort((a, b) => b.results.length - a.results.length || a.key.localeCompare(b.key))
  }
  return out
}
